'use client';

import { Box, VStack } from '@terra/ui-styled-system';
import { memo, useContext, useEffect, useMemo, useRef } from 'react';
import { useTranslations } from 'next-intl';
import { getMessageContentPart } from '../utils/getVercelMessageContentPart';
import type { TerraUIMessagePart } from '@terra/shared-dto-chat-types';
import { MarkdownPreview } from '../../markdown-preview';
import {
  TraceCollapsible,
  TraceCollapsibleContent,
  TraceCollapsibleTrigger,
  TraceLine,
  useTraceOpen,
} from './trace';
import {
  CHAT_STEP_BODY_FONT_SIZE_PX,
  CHAT_STEP_LABEL_COLOR,
} from './chat-nesting-layout';
import { ChatExpansionContext } from '../expansion/chat-expansion-state';

const STREAMING_REASONING_MAX_HEIGHT = '240px';

type ReasoningTimingMetadata = {
  reasoningStartedAt?: number;
  reasoningEndedAt?: number;
  parentToolCallId?: string;
};

const formatThoughtDurationMs = (durationMs: number): string => {
  if (durationMs < 1000) {
    return `${Math.round(durationMs)}ms`;
  }

  return `${Math.round(durationMs / 1000)}s`;
};

const getThoughtDurationLabel = ({
  startedAt,
  endedAt,
}: {
  startedAt: unknown;
  endedAt: unknown;
}): string | undefined => {
  if (
    typeof startedAt !== 'number' ||
    typeof endedAt !== 'number' ||
    !Number.isFinite(startedAt) ||
    !Number.isFinite(endedAt) ||
    endedAt < startedAt ||
    (startedAt === 0 && endedAt === 0)
  ) {
    return undefined;
  }

  return formatThoughtDurationMs(endedAt - startedAt);
};

type ThinkingBlockProps = {
  message: TerraUIMessagePart;
  expansionKey?: string;
};

function ThinkingBlockComponent({ message, expansionKey }: ThinkingBlockProps) {
  const t = useTranslations('designSystem.chat');
  const { reasoningText, isStreaming, isChildThought, durationLabel } =
    useMemo(() => {
      if (message?.type !== 'reasoning') {
        return {
          reasoningText: undefined,
          isStreaming: false,
          isChildThought: false,
          durationLabel: undefined,
        };
      }

      const timing = (
        message.providerMetadata as
          | { metadata?: ReasoningTimingMetadata }
          | undefined
      )?.metadata;

      const parentToolCallId = timing?.parentToolCallId;

      return {
        reasoningText: getMessageContentPart(message, 'reasoning') ?? '',
        isStreaming: message.state === 'streaming',
        isChildThought:
          typeof parentToolCallId === 'string' && parentToolCallId.length > 0,
        durationLabel: getThoughtDurationLabel({
          startedAt: timing?.reasoningStartedAt,
          endedAt: timing?.reasoningEndedAt,
        }),
      };
    }, [message]);

  const expansionStore = useContext(ChatExpansionContext);
  const { isOpen: isExpanded, onOpenChange: handleOpenChange } = useTraceOpen({
    running: isChildThought ? false : isStreaming,
    initialUserOverride:
      isChildThought || !expansionKey
        ? undefined
        : expansionStore?.read(expansionKey),
    onUserChange: (open) => {
      if (expansionKey) {
        expansionStore?.write(expansionKey, open);
      }
    },
  });

  const scrollContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const scrollContainer = scrollContainerRef.current;

    if (!isStreaming || !isExpanded || !scrollContainer) {
      return;
    }

    scrollContainer.scrollTop = scrollContainer.scrollHeight;
  }, [reasoningText, isStreaming, isExpanded]);

  if (!reasoningText) {
    return null;
  }

  const finishedLabel =
    durationLabel === undefined
      ? t('thought')
      : `${t('thought')} ${durationLabel}`;
  const label = isStreaming ? `${t('thinking')}...` : finishedLabel;

  return (
    <TraceCollapsible open={isExpanded} onOpenChange={handleOpenChange}>
      <TraceCollapsibleTrigger>
        <TraceLine
          title={label}
          canExpand
          isExpanded={isExpanded}
          status={isStreaming ? 'running' : undefined}
        />
      </TraceCollapsibleTrigger>
      <TraceCollapsibleContent open={isExpanded}>
        <VStack gap="0" pr={2} pt={1} mt={1} alignItems="stretch">
          <Box
            ref={scrollContainerRef}
            color={CHAT_STEP_LABEL_COLOR}
            fontWeight="400"
            css={{ '& *': { color: 'inherit !important' } }}
            maxH={isStreaming ? STREAMING_REASONING_MAX_HEIGHT : undefined}
            overflowY={isStreaming ? 'auto' : undefined}
            data-streaming={isStreaming || undefined}
          >
            <MarkdownPreview
              source={reasoningText}
              compact
              fontSize={CHAT_STEP_BODY_FONT_SIZE_PX}
            />
          </Box>
        </VStack>
      </TraceCollapsibleContent>
    </TraceCollapsible>
  );
}

export const ThinkingBlock = memo(ThinkingBlockComponent);
